/**
 * Pure helpers for the command center: number and date formatting,
 * the chart geometry that is worth testing, each card's CSV rows, and the
 * prompt "Ask Amoreg" starts a chat with.
 *
 * @module components/commandCenter/commandCenterLogic
 */
import type { CommandCenterSnapshot, Figure } from "../../state/commandCenter";
import {
  describePeriod,
  previousPeriodLabel,
  type CommandCenterPeriod,
} from "./commandCenterPeriod";

// ── Formatting ─────────────────────────────────────────────────────────

const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
const whole = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });

/** 1,284 / 12.9K / 4.2M: exact below ten thousand, compact above. */
export function formatCount(value: number): string {
  if (!Number.isFinite(value)) return "–";
  return Math.abs(value) < 10_000 ? whole.format(value) : compact.format(value);
}

export function formatGbp(value: number): string {
  if (!Number.isFinite(value)) return "–";
  if (Math.abs(value) >= 10_000) return `£${compact.format(value)}`;
  return `£${value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatPercent(share: number): string {
  if (!Number.isFinite(share)) return "–";
  return `${Math.round(share * 100)}%`;
}

export function formatDays(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "–";
  if (value < 1) return "< 1 day";
  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  return `${rounded} ${rounded === 1 ? "day" : "days"}`;
}

/** "↑ 5%" / "↓ 12%" / null when there is no previous period to compare with. */
export function formatDelta(delta: number | null): string | null {
  if (delta === null || !Number.isFinite(delta)) return null;
  const percent = Math.round(Math.abs(delta) * 100);
  if (percent === 0) return "0%";
  return `${delta > 0 ? "↑" : "↓"} ${percent}%`;
}

/**
 * Whether a change is good news. Most figures want to go up; time to resolve
 * and open findings want to go down. A flat or unknown change is neutral.
 */
export function deltaTone(delta: number | null, upIsGood = true): "good" | "bad" | "neutral" {
  if (delta === null || !Number.isFinite(delta) || Math.round(delta * 100) === 0) return "neutral";
  return delta > 0 === upIsGood ? "good" : "bad";
}

/** "Marinus Trustees' command center" / "Acme's command center". */
export function commandCenterTitle(orgName: string | null | undefined): string {
  const name = orgName?.trim();
  if (!name) return "Command center";
  return `${name}${/s$/i.test(name) ? "’" : "’s"} command center`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const parts = (day: string) => {
  const date = new Date(`${day}T00:00:00Z`);
  return {
    month: date.getUTCMonth(),
    date: date.getUTCDate(),
    weekday: date.getUTCDay(),
    year: date.getUTCFullYear(),
  };
};

/** "5 Oct" */
export function formatDay(day: string): string {
  const { month, date } = parts(day);
  return `${date} ${MONTHS[month] ?? ""}`;
}

/** "Mon 5 Oct" */
export function formatDayLong(day: string): string {
  const { weekday } = parts(day);
  return `${WEEKDAYS[weekday] ?? ""} ${formatDay(day)}`;
}

/** "Week of 5 Oct" or the day itself. */
export function formatBucket(start: string, granularity: "day" | "week"): string {
  return granularity === "week" ? `Week of ${formatDay(start)}` : formatDayLong(start);
}

/**
 * Where the x axis puts its labels for a run of days: weekdays over a week,
 * every seventh day (counted back from the last) up to about two months,
 * each month's first day up to about a year, and every third month beyond.
 */
export function axisTicks(
  days: ReadonlyArray<string>,
): ReadonlyArray<{ index: number; label: string }> {
  const count = days.length;
  if (count <= 8)
    return days.map((day, index) => ({ index, label: WEEKDAYS[parts(day).weekday] ?? "" }));
  if (count <= 62) {
    return days.flatMap((day, index) =>
      (count - 1 - index) % 7 === 0 ? [{ index, label: formatDay(day) }] : [],
    );
  }
  const everyMonths = count <= 400 ? 1 : 3;
  return days.flatMap((day, index) => {
    const { date, month, year } = parts(day);
    if (date !== 1 || month % everyMonths !== 0) return [];
    const label = month === 0 && count > 300 ? `${MONTHS[month]} ${year}` : (MONTHS[month] ?? "");
    return [{ index, label }];
  });
}

// ── Chart geometry ─────────────────────────────────────────────────────

/** Trailing mean over `window` points; the first points average what exists. */
export function rollingMean(values: ReadonlyArray<number>, window: number): ReadonlyArray<number> {
  const size = Math.max(1, Math.floor(window));
  let sum = 0;
  return values.map((value, index) => {
    sum += value;
    const leaving = index - size;
    if (leaving >= 0) sum -= values[leaving] ?? 0;
    return sum / Math.min(index + 1, size);
  });
}

/** A clean ceiling for an axis: 1, 2, 2.5 or 5 times a power of ten. */
export function niceCeiling(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  const fraction = value / power;
  const step =
    fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return step * power;
}

/**
 * Four even steps to a clean top: each step 1, 2 or 5 times a power of ten,
 * so the axis reads 0 / 10 / 20 / 30 / 40 and never 12.5 or 38.
 */
export function axisScale(
  largest: number,
  steps = 4,
): { readonly max: number; readonly ticks: ReadonlyArray<number> } {
  const raw = Number.isFinite(largest) && largest > 0 ? largest / steps : 0.25;
  const power = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / power;
  const unit = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  // Counts never need fractional steps.
  const step = Math.max(unit * power, largest >= 1 ? 1 : 0);
  return {
    max: step * steps,
    ticks: Array.from({ length: steps + 1 }, (_, index) => index * step),
  };
}

/**
 * Smooth path through points (monotone cubic, Fritsch–Carlson), so the line
 * never overshoots between two days the way a plain spline would.
 */
export function monotonePath(points: ReadonlyArray<readonly [number, number]>): string {
  const n = points.length;
  if (n === 0) return "";
  const [x0, y0] = points[0]!;
  if (n === 1) return `M${x0},${y0}`;
  const dx: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const [xa, ya] = points[i]!;
    const [xb, yb] = points[i + 1]!;
    dx.push(xb - xa);
    slope.push(xb === xa ? 0 : (yb - ya) / (xb - xa));
  }
  const tangent: number[] = [slope[0] ?? 0];
  for (let i = 1; i < n - 1; i++) {
    const a = slope[i - 1] ?? 0;
    const b = slope[i] ?? 0;
    tangent.push(
      a * b <= 0
        ? 0
        : (3 * (dx[i - 1]! + dx[i]!)) /
            ((2 * dx[i]! + dx[i - 1]!) / a + (dx[i]! + 2 * dx[i - 1]!) / b),
    );
  }
  tangent.push(slope[n - 2] ?? 0);
  let path = `M${x0.toFixed(2)},${y0.toFixed(2)}`;
  for (let i = 0; i < n - 1; i++) {
    const [xa, ya] = points[i]!;
    const [xb, yb] = points[i + 1]!;
    const h = dx[i]! / 3;
    path += ` C${(xa + h).toFixed(2)},${(ya + h * tangent[i]!).toFixed(2)} ${(xb - h).toFixed(2)},${(yb - h * tangent[i + 1]!).toFixed(2)} ${xb.toFixed(2)},${yb.toFixed(2)}`;
  }
  return path;
}

// ── CSV ────────────────────────────────────────────────────────────────

export type CsvValue = string | number | null;

const csvCell = (value: CsvValue): string => {
  if (value === null) return "";
  if (typeof value === "number")
    return Number.isFinite(value) ? String(Math.round(value * 10_000) / 10_000) : "";
  // A name is data, never a formula: a leading = + - @ would run in a spreadsheet.
  const text = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};

export function toCsv(
  header: ReadonlyArray<string>,
  rows: ReadonlyArray<ReadonlyArray<CsvValue>>,
): string {
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

export interface CardTable {
  readonly header: ReadonlyArray<string>;
  readonly rows: ReadonlyArray<ReadonlyArray<CsvValue>>;
}

const share = (value: number) => Math.round(value * 1000) / 10;

/** Each card's rows: the table view shows them and the download writes them. */
export function cardTables(snapshot: CommandCenterSnapshot) {
  const { activity, productUsage, powerUsers, compliance, spend } = snapshot;
  return {
    activeUsers: {
      header: ["Date", "Active users", "Same day, previous period"],
      rows: activity.daily.map((day) => [day.date, day.activeUsers, day.previousActiveUsers]),
    },
    messages: {
      header: ["Date", "Messages sent", "Same day, previous period"],
      rows: activity.daily.map((day) => [day.date, day.messages, day.previousMessages]),
    },
    productUsage: {
      header: [
        "Surface",
        "Members using it",
        "Share of members (%)",
        "Previous period (%)",
        "Needs attention",
      ],
      rows: productUsage.surfaces.map((surface) => [
        surface.label,
        surface.users,
        share(surface.share),
        share(surface.previousShare),
        surface.needsAttention ? "yes" : "no",
      ]),
    },
    powerUsers: {
      header: ["Member", "Active days", "Messages sent"],
      rows: powerUsers.map((user) => [user.name, user.activeDays, user.messages]),
    },
    findings: {
      header: [
        snapshot.window.granularity === "week" ? "Week starting" : "Date",
        "Opened",
        "Resolved",
      ],
      rows: compliance.flow.map((bucket) => [bucket.start, bucket.opened, bucket.resolved]),
    },
    severity: {
      header: ["Severity", "Open findings"],
      rows: compliance.severity.map((row) => [row.severity, row.open]),
    },
    pace: {
      header: ["Measure", "This period", "Previous period"],
      rows: [
        [
          "Mean days to resolve",
          compliance.meanDaysToResolve.value,
          compliance.meanDaysToResolve.previous,
        ],
        ["Findings resolved", compliance.resolved.value, compliance.resolved.previous],
        ["Suggestions applied", compliance.recordUpdates.applied, null],
        ["Suggestions skipped", compliance.recordUpdates.skipped, null],
        ["Suggestions waiting", compliance.recordUpdates.pending, null],
        ["Inspector runs", compliance.inspectorRuns.value, compliance.inspectorRuns.previous],
      ],
    },
    spend: {
      header: [
        snapshot.window.granularity === "week" ? "Week starting" : "Date",
        "Tokens",
        "Cost (GBP)",
      ],
      rows: (spend?.flow ?? []).map((bucket) => [
        bucket.start,
        bucket.tokens,
        Math.round(bucket.costGbp * 100) / 100,
      ]),
    },
  } satisfies Record<string, CardTable>;
}

export type CardKey = keyof ReturnType<typeof cardTables>;

export function csvFileName(
  orgSlug: string,
  card: CardKey,
  snapshot: Pick<CommandCenterSnapshot, "window">,
): string {
  const safe = (text: string) =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  return `${safe(orgSlug) || "organization"}-${safe(card)}-${snapshot.window.since}-to-${snapshot.window.until}.csv`;
}

// ── Ask Amoreg ─────────────────────────────────────────────────────────

const describeFigure = (
  figure: Figure,
  period: CommandCenterPeriod,
  format: (value: number) => string = formatCount,
) => {
  const delta = formatDelta(figure.delta);
  return delta === null
    ? format(figure.value)
    : `${format(figure.value)} (${delta} vs the ${previousPeriodLabel(period)})`;
};

/** The chat prompt that hands these figures to Amoreg and asks what to do next. */
export function askAmoregPrompt(
  snapshot: CommandCenterSnapshot,
  orgName: string,
  period: CommandCenterPeriod,
): string {
  const { activity, productUsage, compliance, spend } = snapshot;
  const low = productUsage.surfaces
    .filter((surface) => surface.needsAttention)
    .map((surface) => surface.label);
  const severity = compliance.severity
    .filter((row) => row.open > 0)
    .map((row) => `${row.open} ${row.severity}`)
    .join(", ");
  const lines = [
    `Here is ${orgName}'s command center for ${describePeriod(period)} (${snapshot.window.since} to ${snapshot.window.until}):`,
    `- Active users: ${describeFigure(activity.activeUsers, period)} of ${productUsage.members} members.`,
    `- Messages sent: ${describeFigure(activity.messages, period)}.`,
    low.length > 0
      ? `- Used by fewer than ${formatPercent(productUsage.attentionBelow)} of members: ${low.join(", ")}.`
      : null,
    `- Open findings: ${compliance.openFindings}${severity ? ` (${severity})` : ""}; ${compliance.opened.value} opened and ${compliance.resolved.value} resolved in this period.`,
    `- Mean time to resolve: ${formatDays(compliance.meanDaysToResolve.value)}.`,
    `- Suggestions: ${compliance.recordUpdates.applied} applied, ${compliance.recordUpdates.skipped} skipped, ${compliance.recordUpdates.pending} waiting.`,
    spend ? `- Model spend: ${describeFigure(spend.costGbp, period, formatGbp)}.` : null,
    "",
    "What should we focus on first to close our compliance gaps and get more of the team using Amoreg? Give me three concrete next steps.",
  ];
  return lines.filter((line): line is string => line !== null).join("\n");
}
